import React, { useMemo, useRef } from "react";
import {
  Box,
  Button,
  cn,
  EntitySelectorTrigger,
  Icon,
  Text,
  useEntitySelector,
  type EntitySelection,
  type IconName,
} from "@salestrak/ui";

import {
  EntityGroupTag,
  EntitySelectionHover,
  EntityTag,
  type OnViewProfile,
} from "#app/components/entity";
import { type EntityData } from "#app/lib/entity-visuals";
import { type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";

// The hover wraps the value INSIDE the button (below the popover trigger) so
// the click still reaches the picker and the hover popover never steals it.

type ButtonVariant = React.ComponentProps<typeof Button>["variant"];
type ButtonSize = React.ComponentProps<typeof Button>["size"];

function toButtonSize(size: SelectorSize | undefined): ButtonSize {
  switch (size) {
    case "xs":
      return "xs";
    case "sm":
      return "sm";
    case "lg":
    case "xl":
      return "lg";
    default:
      return "default";
  }
}

export type SelectionEntityTriggerProps = Omittable<{
  toEntity: (sel: EntitySelection) => EntityData;
  placeholder: string;
  title?: string;
  emptyIcon?: IconName;
  buttonVariant?: ButtonVariant;
  size?: SelectorSize;
  disabled?: boolean;
  // Read-only: render the current selection as a static, full-color value
  // (no picker, no press, no hover/cursor) instead of a dimmed disabled
  // button. Use for view/comment-tier viewers who can see but not edit.
  readOnly?: boolean;
  fill?: boolean;
  showChevron?: boolean;
  /** De-emphasize the selected value's label (muted text). */
  valueMuted?: boolean;
  // Route hover-list presses here instead of the global detail dialog, which
  // would unmount a transient popover the selector might sit inside.
  onViewProfile?: OnViewProfile;
}>;

export function SelectionEntityTrigger({
  toEntity,
  placeholder,
  title,
  emptyIcon,
  buttonVariant = "ghost",
  size = "sm",
  disabled,
  readOnly = false,
  fill,
  showChevron = false,
  valueMuted,
  onViewProfile,
}: SelectionEntityTriggerProps) {
  const { selections, open } = useEntitySelector();

  // Freeze the displayed selection while the picker is open so the trigger
  // doesn't resize and shift the popover anchor.
  const snapshotRef = useRef(selections);
  if (!open) snapshotRef.current = selections;
  const displayed = snapshotRef.current;

  const entities = useMemo(() => displayed.map(toEntity), [displayed, toEntity]);

  const primary = entities[0];
  const extra = entities.length - 1;

  // Read-only: a plain row with no EntitySelectorTrigger/Button — so there's
  // no press to open the picker and no cursor/hover-bg/dim of an editable
  // control. The hover preview (EntitySelectionHover) is kept, so viewers can
  // still hover the value to peek at the entities. Full-color value so it
  // reads as information; padding matches the ghost trigger's text inset so it
  // lines up with the editable state.
  if (readOnly) {
    return (
      <Box direction="row" align="center" gap="xs" paddingX="sm" height={32}>
        {primary ? (
          extra > 0 ? (
            <EntityGroupTag
              entities={entities}
              variant="expanded"
              title={title}
              interactive={false}
              onViewProfile={onViewProfile}
            />
          ) : (
            <EntitySelectionHover
              entities={entities}
              title={title}
              onViewProfile={onViewProfile}
            >
              <Box shrink>
                <EntityTag
                  entity={primary}
                  weight="regular"
                  mutedLabel={valueMuted}
                  interactive={false}
                />
              </Box>
            </EntitySelectionHover>
          )
        ) : emptyIcon ? (
          <>
            <Box width={20} height={20} align="center" justify="center">
              <Icon name={emptyIcon} size="md" color="muted" />
            </Box>
            <Text size="sm" variant="muted" truncate={1}>
              {placeholder}
            </Text>
          </>
        ) : (
          <Text size="sm" variant="muted" truncate={1}>
            {placeholder}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <EntitySelectorTrigger
      disabled={disabled}
      render={
        <Button
          variant={buttonVariant}
          size={toButtonSize(size)}
          disabled={disabled}
          className={cn("justify-start font-normal", fill && "w-full")}
        >
          {primary ? (
            extra > 0 ? (
              // Multi-selection: the first-entity-pill + count-chip group, never
              // a hand-rolled "+N" text. The trigger button owns the press.
              <EntityGroupTag
                entities={entities}
                variant="expanded"
                title={title}
                interactive={false}
                hoverDisabled={open}
                onViewProfile={onViewProfile}
              />
            ) : (
              <EntitySelectionHover
                entities={entities}
                title={title}
                disabled={open}
                onViewProfile={onViewProfile}
              >
                <Box shrink>
                  <EntityTag
                    entity={primary}
                    weight="regular"
                    mutedLabel={valueMuted}
                    interactive={false}
                  />
                </Box>
              </EntitySelectionHover>
            )
          ) : emptyIcon ? (
            <>
              <Box width={20} height={20} align="center" justify="center">
                <Icon name={emptyIcon} size="md" color="muted" />
              </Box>
              <Text size="sm" variant="muted" truncate={1}>
                {placeholder}
              </Text>
            </>
          ) : (
            <Text size="sm" variant="muted" truncate={1}>
              {placeholder}
            </Text>
          )}
          {showChevron ? (
            <Icon name="chevron-down" size="xs" color="muted" />
          ) : null}
        </Button>
      }
    />
  );
}
